import React from 'react';
import PropTypes from 'prop-types';
import { fallbackTheme } from '../theming/fallbackTheme';
import { useTheme } from '../theming';
import { responsivePropType } from '../utils/responsiveProp';
import { StyledSpinner } from './Spinner.styles';
import { useMedia } from '../utils/useMedia';
import { SpinnerProps, SpinnerSize } from './types';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { remToPx } from '../utils';

const { size } = tokens;

export const spinnerSizeMap: Record<SpinnerSize, number> = {
  small: remToPx(parseFloat(size.x5)),
  medium: remToPx(parseFloat(size.x6)),
  large: remToPx(parseFloat(size.x8)),
  xlarge: remToPx(parseFloat(size.x12)),
  xxlarge: remToPx(parseFloat(size.x16)),
};

const strokeWidthThicknessRatio = 10;

const _Spinner = (
  { size = 'small', strokeWidth, color = 'default' }: SpinnerProps,
  ref?: React.Ref<HTMLDivElement>
) => {
  const { getCurrentValue } = useMedia();
  const currentSizeValue = getCurrentValue(size, 'small');
  const isSpinnerSizeValue = !(typeof currentSizeValue === 'number');
  const spinnerSize = isSpinnerSizeValue ? spinnerSizeMap[currentSizeValue] : currentSizeValue;

  if (typeof size === 'number') {
    console.warn(
      'Spinner component: It seems like you have passed a custom number size to Spinner. Custom number sizes are deprecated and will be removed in the next major version, please use one of the predefined sizes `small`, `medium`, `large`, `xlarge`, `xxlarge`'
    );
  }

  if (color.includes('#')) {
    console.warn(
      'Spinner component: It seems like you have passed a custom color to Spinner. Custom colors are deprecated and will be removed in the next major version, please use `default` or `inverse` instead'
    );
  }

  if (strokeWidth) {
    console.warn(
      'Spinner component: It seems like you have passed `strokeWidth` property to Spinner. `strokeWidth` property is deprecated and will be removed in the next major version.'
    );
  }

  const currentTheme = useTheme();
  const spinnerColor = color.includes('#')
    ? color
    : {
        default: fallbackTheme(currentTheme).color.neutral.onSurfaceDefault,
        inverse: fallbackTheme(currentTheme).color.neutral.onSurfaceInverse,
      }[color];

  return (
    <StyledSpinner
      ref={ref}
      style={{ width: spinnerSize, height: spinnerSize }}
      role="img"
      aria-label="Spinner"
      color={spinnerColor}
      size={spinnerSize}
      strokeWidth={spinnerSize / strokeWidthThicknessRatio}
    />
  );
};

export const Spinner = React.forwardRef(_Spinner);

Spinner.propTypes = {
  color: PropTypes.string,
  size: responsivePropType(
    PropTypes.oneOf(['small', 'medium', 'large', 'xlarge', 'xxlarge'] as const)
  ),
  strokeWidth: PropTypes.number,
};
